import React, { useState, useCallback } from 'react';
import GoogleCalendarView from './components/GoogleCalendarView';
import EAControlPanel from './components/EAControlPanel';
import UploadPanel from './components/UploadPanel';
import ArchivariusPanel from './components/ArchivariusPanel';
import ChatAgent from './components/ChatAgent';
import NorthStarPanel from './components/NorthStarPanel';
import ReleasePanel from './components/ReleasePanel';
import MorningBrief from './components/MorningBrief';
import AnimatedBackground from './components/AnimatedBackground';
import './App.css';
import { openInWindow } from './hooks/useCalendarSync';

const API = 'https://calendar.nareea.co.uk';

function App() {
  const params = new URLSearchParams(window.location.search);
  const standalonePanel = params.get('panel');
  const panelTitles = {
    'calendar':'Calendar — Nareea','ea-panel':'EA Dashboard — Nareea',
    'morning-brief':'Morning Brief — Nareea','north-star':'North Star — Nareea',
    'chat':'Chat Agent — Nareea','archivarius':'Archivarius — Nareea',
    'upload':'Upload — Nareea','release':'Release — Nareea',
  };
  if (standalonePanel && panelTitles[standalonePanel]) document.title = panelTitles[standalonePanel];
  const [viewMode, setViewMode] = useState(standalonePanel || 'calendar');
  const [conflictResolved, setConflictResolved] = useState(false);
  const [resolution, setResolution] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [navigateDate, setNavigateDate] = useState(null);
  const handleNavigate = (view, date) => {
    setViewMode(view);
    if (date) setNavigateDate(date + '?' + Date.now());
  };

  const handleResolve = (action) => {
    setResolution(action);
    setConflictResolved(true);
    setCalendarRefreshKey(k => k + 1);
  };

  const handleCalendarRefresh = useCallback(() => {
    setCalendarRefreshKey(k => k + 1);
  }, []);

  const handleGoogleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await fetch(`${API}/api/google/sync`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setSyncMsg(`Synced ${data.count} events from Google Calendar`);
        setCalendarRefreshKey(k => k + 1);
        setTimeout(() => setSyncMsg(''), 4000);
      } else {
        setSyncMsg(`Error: ${data.error}`);
      }
    } catch (err) {
      setSyncMsg('Sync failed — check Google auth');
    } finally {
      setSyncing(false);
    }
  };

  const navItems = [
    { id: 'calendar',       label: 'Calendar' },
    { id: 'morning-brief',  label: 'Morning Brief' },
    { id: 'north-star',     label: 'North Star' },
    { id: 'ea-panel',       label: 'EA Dashboard' },
    { id: 'chat',           label: 'Chat Agent' },
    { id: 'archivarius',    label: 'Archivarius' },
    { id: 'upload',         label: 'Upload' },
    { id: 'release',        label: 'Release for Luke' },
  ];

  const wideViews = ['calendar', 'north-star'];
  const isWide = wideViews.includes(viewMode);

  if (standalonePanel) {
    const panelMap = {
      'calendar':      <GoogleCalendarView conflictResolved={conflictResolved} resolution={resolution} navigateDate={navigateDate} />,
      'ea-panel':      <EAControlPanel onResolve={handleResolve} conflictResolved={conflictResolved} onCalendarRefresh={handleCalendarRefresh} onNavigate={handleNavigate} />,
      'morning-brief': <MorningBrief />,
      'north-star':    <NorthStarPanel />,
      'chat':          <ChatAgent onCalendarUpdate={() => setCalendarRefreshKey(k => k + 1)} />,
      'archivarius':   <ArchivariusPanel />,
      'upload':        <UploadPanel onUploadSuccess={() => setCalendarRefreshKey(k => k + 1)} />,
      'release':       <ReleasePanel />,
    };
    return (
      <div className="app" style={{ background: '#0f0f1a' }}>
        <AnimatedBackground />
        <div style={{ flex:1, overflowY:'auto', position:'relative', zIndex:1 }}>
          {panelMap[standalonePanel] || <div style={{color:'#fff',padding:40}}>Unknown panel</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <AnimatedBackground />

      <nav className="app-navigation">
        {navItems.map(item => (
          <span key={item.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <button
            className={`nav-btn ${viewMode === item.id ? 'active' : ''}`}
            onClick={() => setViewMode(item.id)}
          >{item.label}</button>
          <button
            onClick={() => openInWindow(item.id)}
            title="Open in separate window"
            style={{ background:'none', border:'none', color:'rgba(255,255,255,0.25)', cursor:'pointer', fontSize:13, padding:'0 3px', lineHeight:1 }}
            onMouseEnter={e => e.target.style.color='rgba(255,255,255,0.7)'}
            onMouseLeave={e => e.target.style.color='rgba(255,255,255,0.25)'}
          >⇱</button>
        </span>
        ))}
        <button
          className="nav-btn"
          onClick={handleGoogleSync}
          disabled={syncing}
          style={{ marginLeft: 'auto', borderColor: syncing ? 'rgba(255,255,255,0.1)' : 'rgba(100,220,140,0.4)', color: syncing ? 'rgba(255,255,255,0.3)' : '#6fdc8c' }}
        >
          {syncing ? 'Syncing...' : 'Sync Google'}
        </button>
      </nav>

      {syncMsg && (
        <div style={{
          background: syncMsg.includes('Error') || syncMsg.includes('failed') ? 'rgba(234,67,53,0.2)' : 'rgba(52,168,83,0.2)',
          color: syncMsg.includes('Error') || syncMsg.includes('failed') ? '#ff6b6b' : '#6fdc8c',
          padding: '8px 20px', fontSize: 13, textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          {syncMsg}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', position: 'relative', zIndex: 1 }}>
        {isWide ? (
          <>
            {viewMode === 'calendar' && (
              <GoogleCalendarView
                key={calendarRefreshKey}
                conflictResolved={conflictResolved}
                resolution={resolution}
                navigateDate={navigateDate}
              />
            )}
            {viewMode === 'north-star' && <NorthStarPanel />}
          </>
        ) : (
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' }}>
            {viewMode === 'morning-brief' && <MorningBrief />}
            {viewMode === 'ea-panel' && (
              <EAControlPanel
                onResolve={handleResolve}
                conflictResolved={conflictResolved}
                onCalendarRefresh={handleCalendarRefresh}
                onNavigate={handleNavigate}
              />
            )}
            {viewMode === 'chat' && (
              <ChatAgent onCalendarUpdate={() => setCalendarRefreshKey(k => k + 1)} />
            )}
            {viewMode === 'archivarius' && <ArchivariusPanel />}
            {viewMode === 'upload' && (
              <UploadPanel onUploadSuccess={() => setCalendarRefreshKey(k => k + 1)} />
            )}
            {viewMode === 'release' && <ReleasePanel />}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
